import { useState, useEffect } from "react";
import { ConfigProvider, Select, Image, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import Header from "../../component/menu/Header";
import { constants } from "../../constant/constants";
import SideMenu from "../../component/menu/SideMenu";
import useUserStore from "../../stores/store";
import CustomLoader from "../../component/menu/CustomLoader";
import ApiService from "../../utils/ApiService";
import { preparePlaceDropdown, prepareStaffDropdown, getTablesWithFloorId } from "../../services/table-management/waiter.service";
import { useLocation } from "react-router-dom";


export default function WaiterAssign() {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const paramRestaurantId = searchParams.get("restaurantId");
    const paramBranchId = searchParams.get('branchId');

    const { user, restaurantId, showNotification, networkCall, setNetworkCall, isSuperAdmin } = useUserStore();
    const [placeDropdown, setPlaceDropdown] = useState([]);
    const [staffDropdown, setStaffDropdown] = useState([]);
    const [floorAndTables, setFloorAndTables] = useState([]);
    const [assignTables, setAssignTables] = useState([]);
    const [currentFloorId, setCurrentFloorId] = useState("");
    const [initStaff, setInitStaff] = useState({});

    function WaiterSkeleton() {

        return (
            <div className="flex items-center space-x-[42px] mb-[14px]">
                <div className="w-[23.08917197%] h-[36px] drop-shadow-sm flex items-center justify-center">
                    <Skeleton.Input active={true} size={"square"} block={true} />
                </div>
                <div className="w-[47.13375796%] h-[36px]">
                    <Skeleton.Input active={true} size={"square"} block={true} />
                </div>
            </div>
        );
    }

    async function getTableAndFloors(params) {
        setNetworkCall(true);
        if (!params) return;
        if (!params.hasOwnProperty("restaurantId")) {
            console.error("restaurantId is required for getting floor dropdown.");
            return;
        }
        try {
            const res = await ApiService.GET_TABLEMANAGEMENT_FLOOR_AND_TABLE(params, user.token.token);
            if (res.code !== 200) {
                showNotification(res.data.message);
                setNetworkCall(false);
                return;
            }
            const { payload } = res;
            const { record } = payload;
            if (!Boolean(record)) {
                showNotification('Something went wrong! Please contact your administrator.');
                setNetworkCall(false);
                return;
            }
            const { floors } = record;
            if (floors.length === 0) {
                showNotification('Please add tables in the floor first!');
                return;
            }

            const initialFloorId = floors[0]?.id || "";
            setCurrentFloorId(initialFloorId);
            setAssignTables(getTablesWithFloorId(floors, initialFloorId));

            setFloorAndTables(floors);
            setPlaceDropdown(preparePlaceDropdown(floors));
        } catch (err) {
            showNotification(err.response.data.message);
            return;
        } finally {
            setNetworkCall(false);
        }
    }

    async function getStaffs(params) {
        setNetworkCall(true);
        if (!params) return;
        if (!params.hasOwnProperty("restaurantId")) {
            console.error("restaurantId is required for getting floor dropdown.");
            return;
        }
        try {
            const res = await ApiService.GET_TABLEMANAGEMENT_STAFF_DROPDOWN(params, user.token.token);
            if (res.code !== 200) {
                showNotification(res.data.message);
                setNetworkCall(false);
                return;
            }
            const { payload } = res;
            const { records } = payload;
            if (Boolean(records) && records.length === 0) {
                showNotification('Something went wrong! Please contact your administrator.');
                setNetworkCall(false);
                return;
            }

            setStaffDropdown(prepareStaffDropdown(records));
            setNetworkCall(false);
            return;
        } catch (err) {
            setNetworkCall(false);
            showNotification(err.response.data.message);
            return;
        }
    }

    async function assignTableAndStaff(params) {
        setNetworkCall(true);

        try {
            const res = await ApiService.TABLEMANAGEMENT_TABLE_AND_STAFF_ASSIGN(params, user.token.token);
            if (res.code !== 200) {
                showNotification(res.message);
                setNetworkCall(false);
                return;
            }

            showNotification("The waiter has been assigned to the table successfully.", false);
            setNetworkCall(false);
            onHandleCancel();
        } catch (err) {
            setNetworkCall(false);
            showNotification(err.response.data.message);
            return;
        }
    }

    function onHandleFloorSelect(value) {
        setCurrentFloorId(value);
        setAssignTables(getTablesWithFloorId(floorAndTables, value, initStaff));
    }

    function onHandleSelectStaff(objId, value, label) {
        setAssignTables((prev) =>
            prev.map((item) =>
                item.objId === objId
                    ? { ...item, staffId: value, staffName: label }
                    : item
            )
        );
    }

    function onHandleCancel() {
        let url = `/waiter/list`;
        if (assignTables.length === 0) {
            url = `/restaurants/${paramRestaurantId}/branch/${paramBranchId}/floor-plan/add`;
        } if (isSuperAdmin && assignTables.length > 0) {
            url = url.concat(`?restaurantId=${paramRestaurantId}&branchId=${paramBranchId}`);
        }
        navigate(url);
    }

    function onHandleSave() {
        if (assignTables.length === 0) {
            showNotification('Please add tables in the floor first!');
            return;
        }
        const assignedTables = assignTables.map(tb => {
            const { objId, tableName, ...rest } = tb;
            return rest;
        });
        const resId = restaurantId || paramBranchId;
        const payload = {
            restaurantId: resId,
            floorId: currentFloorId,
            tables: assignedTables
        }
        assignTableAndStaff(payload);
    }

    useEffect(() => {
        const resId = restaurantId || paramBranchId;

        async function initData() {
            if (resId === '') return;
            await getStaffs({ restaurantId: resId });
            await getTableAndFloors({ restaurantId: resId });
        }

        initData();

    }, [restaurantId, paramBranchId]);

    return (
        <section>
            <div className="flex flex-col">
                <CustomLoader />
                <Header title={""} menu={constants.MENU.TABLE} currentMenuKey={'T_8_S_8'} />
                <div className="flex flex-row bg-prewhite px-[12%]">
                    <SideMenu title={`Waiter Assign`} menu={constants.MENU.TABLE} />
                </div>
                <div className=" mt-[80px] ml-[80px] px-[50px]">
                    <h1 className="pt-[50px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
                        Waiter Assign
                    </h1>

                    <section className="w-[60%] min-h-[628px] pl-[37px] pt-[27px] bg-white">
                        <ConfigProvider
                            theme={{
                                components: {
                                    Select: {
                                        zIndexPopup: 1,
                                    },
                                },
                            }}
                        >

                            {/* select place */}
                            <div className="mb-[29px]">
                                <h1 className="font-p_medium text-[16px] leading-[13px] text-black mb-[5px]">Select Place</h1>
                                <div className="h-[48px] w-1/2">
                                    <Select
                                        className="w-full h-full"
                                        showSearch={true}
                                        onChange={onHandleFloorSelect}
                                        placeholder="Select Place"
                                        optionFilterProp="label"
                                        options={placeDropdown}
                                        value={currentFloorId}
                                    />
                                </div>
                            </div>

                            {/* tables and staffs */}
                            <div className="min-h-[30vh]">
                                {/* header */}
                                <div className="mb-[18px] font-p_medium text-[12px] text-black leading-[13px] flex items-center space-x-[42px]">
                                    <h1 className="w-[23.08917197%]">Table No</h1>
                                    <h1 className="w-[47.13375796%]">Staff Name</h1>
                                </div>

                                {/* body */}
                                <div>

                                    {
                                        assignTables.length > 0 ?
                                            assignTables.map((table) => (
                                                <div key={table.objId} className="flex items-center space-x-[42px] mb-[14px]">
                                                    <div className="w-[23.08917197%] h-[36px] bg-[#FCF1B7] border border-px border-[#E9EBEF] drop-shadow-sm flex items-center justify-center">
                                                        <div className="flex items-center space-x-[12.67px]">
                                                            <Image src="/images/icons/daining-table.svg" alt="table" preview={false} />
                                                            <p className="font-p_bold text-[20px] leading-[24px] text-black">{table.tableName}</p>
                                                        </div>
                                                    </div>
                                                    <div className="w-[47.13375796%] h-[36px]">
                                                        <Select
                                                            className="w-full h-full text-[#828282]"
                                                            showSearch={true}
                                                            onChange={(value, option) => onHandleSelectStaff(table.objId, value, option.label)}
                                                            placeholder="Select Staff"
                                                            optionFilterProp="label"
                                                            options={staffDropdown}
                                                            value={table.staffId}
                                                        />
                                                    </div>
                                                </div>
                                            )) : (
                                                WaiterSkeleton()
                                            )
                                    }
                                </div>
                            </div>

                            <div className="pt-[66px] pb-[34px] flex items-center space-x-[15px]">
                                <button className="bg-[#FA6060] w-[100px] h-[42px] rounded-[5px] font-bold text-white text-[14px] leading-[16px]"
                                    onClick={onHandleCancel}>Cancel</button>
                                <button className="bg-[#2EC114] w-[100px] h-[42px] rounded-[5px] font-bold text-white text-[14px] leading-[16px]"
                                    onClick={onHandleSave}>Save</button>
                            </div>

                        </ConfigProvider>
                    </section>
                </div>
            </div>
        </section>
    );
}
